import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from '@axios'
import { getNamespaceSocket } from '@/composables/useSocket'

function _getMessagingSocket() {
  return getNamespaceSocket('/messaging')
}

function _emitViewConversation(conversationId) {
  const sock = _getMessagingSocket()
  if (sock && sock.connected) {
    sock.emit('viewing_conversation', { conversationId })
  }
}

function _emitLeaveConversation() {
  const sock = _getMessagingSocket()
  if (sock && sock.connected) {
    sock.emit('leave_conversation')
  }
}

export const useMessagingStore = defineStore('messaging', () => {
  // State
  const conversations = ref([])
  const activeConversationId = ref(null)
  const messages = ref([])
  const loadingConversations = ref(false)
  const loadingMessages = ref(false)
  const sendingMessage = ref(false)

  // Total unread count for current user
  const totalUnread = computed(() => {
    // Will be updated by socket events
    return _totalUnread.value
  })
  const _totalUnread = ref(0)

  // Restaurant admin unread count
  const restaurantUnreadCount = ref(0)

  // Active conversation object
  const activeConversation = computed(() =>
    conversations.value.find(c => c._id === activeConversationId.value) || null
  )

  // ─── Actions ──────────────────────────────────────────────────────────────

  async function fetchConversations() {
    loadingConversations.value = true
    try {
      const { data } = await axios.get('/messaging/conversations')
      conversations.value = data.conversations || []
      fetchMemberUnreadCount()
    } catch (err) {
      console.error('fetchConversations error', err)
    } finally {
      loadingConversations.value = false
    }
  }

  async function loadConversation(convId) {
    activeConversationId.value = convId
    _emitViewConversation(convId)
    loadingMessages.value = true
    try {
      const { data } = await axios.get(`/messaging/conversations/${convId}`)
      messages.value = data.messages || []

      // Mark as read — zero out locally for instant UI feedback
      const conv = conversations.value.find(c => c._id === convId)
      if (conv && conv.unread > 0) {
        conv.unread = 0
      }
      markReadLocal()
    } catch (err) {
      console.error('loadConversation error', err)
    } finally {
      loadingMessages.value = false
    }
  }

  async function sendMessage(convId, text, reservationId = null) {
    sendingMessage.value = true
    try {
      const sock = _getMessagingSocket()
      if (sock && sock.connected) {
        // Use socket so all participants receive real-time new_message event
        const message = await new Promise((resolve, reject) => {
          sock.emit('send_message', { conversationId: convId, text, reservationId }, response => {
            if (response.error) reject(new Error(response.error))
            else resolve(response.message)
          })
        })

        // The sender's own message comes back via the ack; push it locally
        // (the new_message socket event deduplicates so no double-add)
        if (message) {
          const exists = messages.value.some(m => m._id === message._id)
          if (!exists) messages.value.push(message)
          updateConversationLastMessage(convId, message)
        }
        
        return message
      } else {
        // Fallback to REST (no real-time for recipient, but message is saved)
        const { data } = await axios.post(`/messaging/conversations/${convId}/messages`, {
          text,
          reservationId,
        })
        if (data.message) {
          messages.value.push(data.message)
          updateConversationLastMessage(convId, data.message)
        }
        
        return data.message
      }
    } catch (err) {
      console.error('sendMessage error', err)
      throw err
    } finally {
      sendingMessage.value = false
    }
  }

  async function deleteMessage(messageId) {
    try {
      const sock = _getMessagingSocket()
      if (sock && sock.connected) {
        await new Promise((resolve, reject) => {
          sock.emit('delete_message', { messageId }, response => {
            if (response && response.error) reject(new Error(response.error))
            else resolve()
          })
        })
      } else {
        await axios.delete(`/messaging/messages/${messageId}`)
      }

      // Apply locally (socket event will also fire but handleMessageDeleted deduplicates)
      const idx = messages.value.findIndex(m => m._id === messageId)
      if (idx !== -1) {
        messages.value[idx] = { ...messages.value[idx], deletedAt: new Date().toISOString() }
      }
    } catch (err) {
      console.error('deleteMessage error', err)
      throw err
    }
  }

  async function markRead(convId) {
    try {
      await axios.put(`/messaging/conversations/${convId}/read`)
      markReadLocal(convId)
    } catch (err) {
      console.error('markRead error', err)
    }
  }

  async function createConversation(params) {
    try {
      const { data } = await axios.post('/messaging/conversations', params)
      if (data.conversation) {
        const existing = conversations.value.find(c => c._id === data.conversation._id)
        if (!existing) {
          conversations.value.unshift(data.conversation)
        }
      }
      
      return data.conversation
    } catch (err) {
      console.error('createConversation error', err)
      throw err
    }
  }

  async function sendRaMessage(convId, restaurantId, text) {
    sendingMessage.value = true
    try {
      const { data } = await axios.post(
        `/messaging/restaurant/${restaurantId}/conversations/${convId}/messages`,
        { text }
      )
      if (data.message) {
        messages.value.push(data.message)
        updateConversationLastMessage(convId, data.message)
      }
      
      return data.message
    } catch (err) {
      console.error('sendRaMessage error', err)
      throw err
    } finally {
      sendingMessage.value = false
    }
  }

  async function fetchMemberUnreadCount() {
    try {
      const { data } = await axios.get('/messaging/member-unread-count')
      _totalUnread.value = data.count || 0
    } catch (err) {
      // Silently fail
    }
  }

  async function fetchRestaurantUnreadCount() {
    try {
      const { data } = await axios.get('/messaging/unread-count')
      restaurantUnreadCount.value = data.count || 0
    } catch (err) {
      // Silently fail — user might not be an RA
    }
  }

  // ─── Socket-driven updates ────────────────────────────────────────────────

  function handleNewMessage(payload) {
    const { message, conversationId } = payload

    // If this is the active conversation, push the message
    if (activeConversationId.value === conversationId) {
      const exists = messages.value.some(m => m._id === message._id)
      if (!exists) {
        messages.value.push(message)
      }

      // Mark as read since it's the active conversation
      markRead(conversationId)
    } else {
      // Increment unread count locally for the badge
      _totalUnread.value++

      // Increment per-conversation unread for inbox indicator
      const conv = conversations.value.find(c => c._id === conversationId)
      if (conv) {
        conv.unread = (conv.unread || 0) + 1
        fetchConversations()
      }
    }

    updateConversationLastMessage(conversationId, message)
  }

  function handleMessageDeleted(payload) {
    const { messageId } = payload
    const idx = messages.value.findIndex(m => m._id === messageId)
    if (idx !== -1) {
      messages.value[idx] = { ...messages.value[idx], deletedAt: new Date().toISOString() }
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function updateConversationLastMessage(convId, message) {
    const idx = conversations.value.findIndex(c => c._id === convId)
    if (idx !== -1) {
      conversations.value[idx] = {
        ...conversations.value[idx],
        lastMessage: {
          text: message.text,
          senderType: message.senderType,
          senderId: message.senderId,
          sentAt: message.createdAt,
        },
        lastMessageAt: message.createdAt,
      }

      // Move to top
      const conv = conversations.value.splice(idx, 1)[0]
      conversations.value.unshift(conv)
    }
  }

  function markReadLocal() {
    // Re-fetch the accurate count from the server after marking read
    fetchMemberUnreadCount()
  }

  function clearActiveConversation() {
    _emitLeaveConversation()
    activeConversationId.value = null
    messages.value = []
  }

  return {
    conversations,
    activeConversationId,
    activeConversation,
    messages,
    loadingConversations,
    loadingMessages,
    sendingMessage,
    totalUnread,
    restaurantUnreadCount,
    fetchConversations,
    loadConversation,
    sendMessage,
    sendRaMessage,
    deleteMessage,
    markRead,
    createConversation,
    fetchMemberUnreadCount,
    fetchRestaurantUnreadCount,
    handleNewMessage,
    handleMessageDeleted,
    clearActiveConversation,
  }
})
